import React, { useEffect, useState } from "react";
import dropdown from "../assets/dropdown.png";
import poolBox from "../assets/poolBox.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useActiveAccount, useActiveWalletChain } from "thirdweb/react";
import { balanceOf, getCurrencyMetadata } from "thirdweb/extensions/erc20";
import { getErc20Contract, getFactoryContract, getLPContract } from "../contexts/thirdWebClient";
import { readContract } from "thirdweb";
import { ethers } from "ethers";

const Pool = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const add = useActiveAccount();
    const [address, setAddress] = useState("");
    const chain = useActiveWalletChain();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (add && add.address && chain) {
            setAddress(add.address);
            getLpListOfUser(add.address, chain);
        }
    }, [add, chain]);

    // Refresh data when navigating back with refresh state
    useEffect(() => {
        if (location.state?.refresh && add && add.address && chain) {
            getLpListOfUser(add.address, chain);
            // Clear the state to prevent re-fetching on subsequent renders
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    const [lpAddress, setLpAddress] = useState([]);

    // Enumerate every pair from the factory on-chain and check the connected
    // wallet's LP balance for each. No backend dependency, so the listing is
    // never stale relative to the chain (e.g. right after add/remove).
    const getLpListOfUser = async (address, chain) => {
        try {
            setLoading(true);
            const factory = getFactoryContract(chain);
            const pairCountRaw = await readContract({
                contract: factory,
                method: "function allPairsLength() view returns (uint256)",
                params: [],
            });
            const pairCount = Number(pairCountRaw);

            const pairs = [];
            for (let i = 0; i < pairCount; i++) {
                const pairAddress = await readContract({
                    contract: factory,
                    method: "function allPairs(uint256) view returns (address)",
                    params: [i],
                });
                pairs.push({ pairAddress });
            }

            setLpAddress(pairs);
            await fetchLpDetails(pairs, address, chain);
        } catch (error) {
            console.log("%c Pool getLpListOfUser error", "color:#ff5b5b", error);
            setLoading(false);
        }
    };

    const [lpDetails, setLpDetails] = useState([]);

    const fetchLpDetails = async (trxData, address, chain) => {
        let myLp = [];
        for (const element of trxData) {
            try {
                const contract = getErc20Contract(element.pairAddress, chain);
                let userBalance = await balanceOf({
                    contract,
                    address: address,
                });

                // Skip pools where the connected wallet has zero LP balance.
                // Compare BigInt directly to catch dust amounts that would
                // format to "0" but still be non-zero on-chain.
                if (userBalance === undefined || userBalance === null || userBalance <= 0n) {
                    continue;
                }

                let currencyMetadata = await getCurrencyMetadata({ contract });
                let pairTotalSupply = await readContract({
                    contract,
                    method: "function totalSupply() view returns (uint256)",
                    params: [],
                });

                // Read token0/token1 directly from the pair (no backend lookup).
                const lpContract = getLPContract(element.pairAddress, chain);
                const token0Address = await readContract({
                    contract: lpContract,
                    method: "function token0() view returns (address)",
                    params: [],
                });
                const token1Address = await readContract({
                    contract: lpContract,
                    method: "function token1() view returns (address)",
                    params: [],
                });

                const Token0contract = getErc20Contract(token0Address, chain);

                let tokenOMetaData = await getCurrencyMetadata({
                    contract: Token0contract,
                });
                let tokenOUserBalance = await balanceOf({
                    contract: Token0contract,
                    address: element.pairAddress,
                });

                const Token1contract = getErc20Contract(token1Address, chain);
                let token1MetaData = await getCurrencyMetadata({
                    contract: Token1contract,
                });
                let token1UserBalance = await balanceOf({
                    contract: Token1contract,
                    address: element.pairAddress,
                });

                let userShare =
                    (Number(
                        ethers.formatUnits(
                            userBalance,
                            currencyMetadata?.decimals,
                        ),
                    ) *
                        100) /
                    Number(
                        ethers.formatUnits(
                            pairTotalSupply,
                            currencyMetadata?.decimals,
                        ),
                    );

                let pairData = {
                    pairAddress: element.pairAddress,
                    token0Address: token0Address,
                    token1Address: token1Address,
                    lpName:
                        tokenOMetaData?.symbol +
                        "-" +
                        token1MetaData?.symbol +
                        " LP",
                    lpTotalSupply: Number(
                        ethers.formatUnits(
                            pairTotalSupply,
                            currencyMetadata?.decimals,
                        ),
                    ),
                    lpUserBalance: Number(
                        ethers.formatUnits(
                            userBalance,
                            currencyMetadata?.decimals,
                        ),
                    ),
                    userShare: userShare,
                    token0Name: tokenOMetaData?.name,
                    token0Symbol: tokenOMetaData?.symbol,
                    token0UserBalance:
                        (userShare *
                            Number(
                                ethers.formatUnits(
                                    tokenOUserBalance,
                                    tokenOMetaData?.decimals,
                                ),
                            )) /
                        100,
                    token1Name: token1MetaData?.name,
                    token1Symbol: token1MetaData?.symbol,
                    token1UserBalance:
                        (userShare *
                            Number(
                                ethers.formatUnits(
                                    token1UserBalance,
                                    token1MetaData?.decimals,
                                ),
                            )) /
                        100,
                };
                console.log(
                    "%c Line:85 🍰 pairData",
                    "color:#6ec1c2",
                    pairData,
                );
                myLp.push(pairData);
            } catch (error) {
                console.log("%c Line:47 🍉 error", "color:#42b983", error);
            }
        }
        console.log("%c Line:148 🍒 myLp", "color:#ea7e5c", myLp);
        setLpDetails(myLp);
        setLoading(false);
    };

    return (
        <div className="max-w-[724px] mx-3 sm:mx-auto mt-10 flex gap-2 items-center justify-center my-12 flex-col p-5 rounded-xl bg-perpx-dark-tertiary/50 backdrop-blur-md border border-perpx-cyan/20">
            <div className=" flex items-center justify-between w-full">
                <h6 className="text-perpx-cyan text-[22px] sm:text-[26px] font-semibold">
                    Positions
                </h6>
                <div className="flex justify-center items-center gap-2">
                    {/* <div className="flex items-center justify-center gap-1 sm:gap-3 bg-tranparent border border-[#3c486b10] rounded-[48px] py-[6px] px-[18px] cursor-pointer">
                        <h5 className="font-semibold text-[14px] sm:text-[16px] text-[#102C57]">
                            More
                        </h5>
                        <img
                            src={dropdown}
                            alt=""
                            className="size-[18px] sm:size-[20px]"
                        />
                    </div> */}
                    <button
                        onClick={() => navigate("/pool/addliquidity/0x/0x")}
                        className="bg-perpx-cyan w-full rounded-[64px] py-[6px] px-[12px] text-perpx-dark text-[14x] sm:text-[16px] font-semibold hover:shadow-perpx-glow transition-all"
                    >
                        New Position
                    </button>
                </div>
            </div>
            {loading ? (
                <div className="rounded-[16px] flex flex-col items-center bg-perpx-dark-secondary/50 gap-4 justify-center overflow-hidden border w-full border-perpx-cyan/20 m-auto min-h-[260px]">
                    <div className="animate-pulse text-perpx-cyan text-[18px] font-semibold">
                        Loading your liquidity positions...
                    </div>
                </div>
            ) : lpDetails && lpDetails.length > 0 ? (
                lpDetails.map((ele) => (
                    <div className="rounded-[16px] overflow-hidden border w-full border-perpx-cyan/20">
                        <div className="bg-perpx-dark-secondary/80 text-perpx-text-primary font-semibold flex justify-start text-[14px] sm:text-[20px] font-normal px-4 py-2 border-b border-perpx-cyan/10">
                            <h3 className="text-perpx-cyan">{ele?.lpName}</h3>
                            <button
                                onClick={() =>
                                    navigate(
                                        `/pool/addliquidity/${ele?.token0Address}/${ele?.token1Address}`,
                                    )
                                }
                                style={{
                                    width: "20%",
                                    "margin-left": "auto",
                                }}
                                className="bg-perpx-cyan w-full rounded-[64px] py-[6px] px-[12px] text-perpx-dark text-[14x] sm:text-[16px] font-semibold hover:shadow-perpx-glow transition-all"
                            >
                                Add
                            </button>
                            {ele?.lpUserBalance > 0 && (
                                <button
                                    onClick={() =>
                                        navigate(
                                            `/remove/liquidity/${ele?.pairAddress}`,
                                        )
                                    }
                                    style={{
                                        width: "20%",
                                        "margin-left": "auto",
                                    }}
                                    className="bg-perpx-dark border border-perpx-cyan/30 w-full rounded-[64px] py-[6px] px-[12px] text-perpx-cyan text-[14x] sm:text-[16px] font-semibold hover:border-perpx-cyan transition-all ml-2"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                        <div className="bg-perpx-dark/90 flex items-center justify-evenly py-3 gap-2">
                            <div className="flex flex-col items-center justify-center gap-2">
                                <p className="font-semibold text-[18px] text-perpx-cyan">
                                    {(ele?.token0UserBalance).toFixed(4)}{" "}
                                    {ele?.token0Symbol}
                                </p>
                                <p className="font-medium text-[15px] text-perpx-text-secondary">
                                    {ele?.token0Name}
                                </p>
                            </div>
                            <div className="flex flex-col items-center justify-center gap-2">
                                <p className="font-semibold text-[18px] text-perpx-cyan">
                                    {(ele?.token1UserBalance).toFixed(4)}{" "}
                                    {ele?.token1Symbol}
                                </p>
                                <p className="font-medium text-[15px] text-perpx-text-secondary">
                                    {ele?.token1Name}
                                </p>
                            </div>
                            <div className="flex flex-col items-center justify-center gap-2">
                                <p className="font-semibold text-[18px] text-perpx-cyan">
                                    {ele?.userShare.toFixed(2)}%
                                </p>
                                <p className="font-medium text-[15px] text-perpx-text-secondary">
                                    Share of Pool
                                </p>
                            </div>
                        </div>
                        {/* <div className="bg-[#f7f7f7] flex items-center justify-evenly py-6 gap-3">
                            <div className="flex flex-col items-center justify-center gap-2">
                                <p className="font-semibold text-[18px] text-[#102C57]">
                                    3376.51
                                </p>
                                <p className="font-medium text-[15px] text-[#3C486B99]">
                                    USDT per ETH
                                </p>
                            </div>
                            <div className="flex flex-col items-center justify-center gap-2">
                                <p className="font-semibold text-[18px] text-[#102C57]">
                                    0.000338823
                                </p>
                                <p className="font-medium text-[15px] text-[#3C486B99]">
                                    ETH per USDT
                                </p>
                            </div>
                            <div className="flex flex-col items-center justify-center gap-2">
                                <p className="font-semibold text-[18px] text-[#102C57]">
                                    99.65%
                                </p>
                                <p className="font-medium text-[15px] text-[#3C486B99]">
                                    Share of Pool
                                </p>
                            </div>
                        </div> */}
                    </div>
                ))
            ) : (
                <div className="rounded-[16px] flex flex-col items-center bg-perpx-dark-secondary/50 gap-4 justify-center overflow-hidden border w-full border-perpx-cyan/20 m-auto min-h-[260px]">
                    <img src={poolBox} alt="" className="opacity-70" />
                    <h5 className="font-medium text-[14px] sm:text-[16px] text-perpx-text-secondary">
                        Your active liquidity positions will appear here
                    </h5>
                </div>
            )}
        </div>
    );
};

export default Pool;
